import { Grid } from '../core/Grid.js';
import { GameType } from '../persistence/types.js';
import { TutorialDefinition, TutorialGameAdapter, MetaTutorialId } from './TutorialEngine.js';
import { BASICS_TUTORIAL } from './meta/basicsTutorial.js';
import { VORTEX_HUB_TUTORIAL } from './meta/vortexHubTutorial.js';
import { createPlainGridTutorialAdapter } from './meta/PlainGridTutorialAdapter.js';
import { createVortexHubTutorialAdapter } from './meta/VortexHubTutorialAdapter.js';
import { MIRRORS_TUTORIAL } from './mirrors/mirrorsTutorial.js';
import { createMirrorsTutorialAdapter, createMirrorsVortexTutorialAdapter } from './mirrors/MirrorsTutorialAdapter.js';
import { LINE_TUTORIAL } from './line/lineTutorial.js';
import { createLineTutorialAdapter, createLineVortexTutorialAdapter } from './line/LineTutorialAdapter.js';
import { KNIGHT_TUTORIAL } from './knight/knightTutorial.js';
import { createKnightTutorialAdapter, createKnightVortexTutorialAdapter } from './knight/KnightTutorialAdapter.js';
import { BRANCHES_TUTORIAL } from './branches/branchesTutorial.js';
import { createBranchesTutorialAdapter, createBranchesVortexTutorialAdapter } from './branches/BranchesTutorialAdapter.js';
import { WALLS_TUTORIAL } from './walls/wallsTutorial.js';
import { createWallsTutorialAdapter, createWallsVortexTutorialAdapter } from './walls/WallsTutorialAdapter.js';
import { IGNITION_TUTORIAL } from './ignition/ignitionTutorial.js';
import { createIgnitionTutorialAdapter, createIgnitionVortexTutorialAdapter } from './ignition/IgnitionTutorialAdapter.js';
import { CROSSING_TUTORIAL } from './crossing/crossingTutorial.js';
import { createCrossingTutorialAdapter, createCrossingVortexTutorialAdapter } from './crossing/CrossingTutorialAdapter.js';
import { FLOOD_TUTORIAL } from './flood/floodTutorial.js';
import { createFloodTutorialAdapter, createFloodVortexTutorialAdapter } from './flood/FloodTutorialAdapter.js';
import { ORBITS_TUTORIAL } from './orbits/orbitsTutorial.js';
import { createOrbitsTutorialAdapter, createOrbitsVortexTutorialAdapter } from './orbits/OrbitsTutorialAdapter.js';
import { SPLITTING_TUTORIAL } from './splitting/splittingTutorial.js';
import { createSplittingTutorialAdapter, createSplittingVortexTutorialAdapter } from './splitting/SplittingTutorialAdapter.js';
import { DELUGE_TUTORIAL } from './deluge/delugeTutorial.js';
import { createDelugeTutorialAdapter, createDelugeVortexTutorialAdapter } from './deluge/DelugeTutorialAdapter.js';
import { ANTENNAS_TUTORIAL } from './antennas/antennasTutorial.js';
import { createAntennasTutorialAdapter, createAntennasVortexTutorialAdapter } from './antennas/AntennasTutorialAdapter.js';
import { TURNS_TUTORIAL } from './turns/turnsTutorial.js';
import { createTurnsTutorialAdapter, createTurnsVortexTutorialAdapter } from './turns/TurnsTutorialAdapter.js';
import { JUMPS_TUTORIAL } from './jumps/jumpsTutorial.js';
import { createJumpsTutorialAdapter, createJumpsVortexTutorialAdapter } from './jumps/JumpsTutorialAdapter.js';
import { MIRRORS_VORTEX_TUTORIAL } from './mirrors/mirrorsVortexTutorial.js';
import { LINE_VORTEX_TUTORIAL } from './line/lineVortexTutorial.js';
import { KNIGHT_VORTEX_TUTORIAL } from './knight/knightVortexTutorial.js';
import { BRANCHES_VORTEX_TUTORIAL } from './branches/branchesVortexTutorial.js';
import { WALLS_VORTEX_TUTORIAL } from './walls/wallsVortexTutorial.js';
import { IGNITION_VORTEX_TUTORIAL } from './ignition/ignitionVortexTutorial.js';
import { CROSSING_VORTEX_TUTORIAL } from './crossing/crossingVortexTutorial.js';
import { FLOOD_VORTEX_TUTORIAL } from './flood/floodVortexTutorial.js';
import { ORBITS_VORTEX_TUTORIAL } from './orbits/orbitsVortexTutorial.js';
import { SPLITTING_VORTEX_TUTORIAL } from './splitting/splittingVortexTutorial.js';
import { DELUGE_VORTEX_TUTORIAL } from './deluge/delugeVortexTutorial.js';
import { ANTENNAS_VORTEX_TUTORIAL } from './antennas/antennasVortexTutorial.js';
import { TURNS_VORTEX_TUTORIAL } from './turns/turnsVortexTutorial.js';
import { JUMPS_VORTEX_TUTORIAL } from './jumps/jumpsVortexTutorial.js';

export interface TutorialEntry {
    definition: TutorialDefinition;
    createAdapter: (canvas: HTMLCanvasElement, grid: Grid) => TutorialGameAdapter;
}

const TUTORIAL_REGISTRY: Partial<Record<GameType, TutorialEntry>> = {
    mirrors: { definition: MIRRORS_TUTORIAL, createAdapter: createMirrorsTutorialAdapter },
    line: { definition: LINE_TUTORIAL, createAdapter: createLineTutorialAdapter },
    knight: { definition: KNIGHT_TUTORIAL, createAdapter: createKnightTutorialAdapter },
    branches: { definition: BRANCHES_TUTORIAL, createAdapter: createBranchesTutorialAdapter },
    walls: { definition: WALLS_TUTORIAL, createAdapter: createWallsTutorialAdapter },
    ignition: { definition: IGNITION_TUTORIAL, createAdapter: createIgnitionTutorialAdapter },
    crossing: { definition: CROSSING_TUTORIAL, createAdapter: createCrossingTutorialAdapter },
    flood: { definition: FLOOD_TUTORIAL, createAdapter: createFloodTutorialAdapter },
    orbits: { definition: ORBITS_TUTORIAL, createAdapter: createOrbitsTutorialAdapter },
    splitting: { definition: SPLITTING_TUTORIAL, createAdapter: createSplittingTutorialAdapter },
    deluge: { definition: DELUGE_TUTORIAL, createAdapter: createDelugeTutorialAdapter },
    antennas: { definition: ANTENNAS_TUTORIAL, createAdapter: createAntennasTutorialAdapter },
    turns: { definition: TURNS_TUTORIAL, createAdapter: createTurnsTutorialAdapter },
    jumps: { definition: JUMPS_TUTORIAL, createAdapter: createJumpsTutorialAdapter },
};

const VORTEX_TUTORIAL_REGISTRY: Partial<Record<GameType, TutorialEntry>> = {
    mirrors: { definition: MIRRORS_VORTEX_TUTORIAL, createAdapter: createMirrorsVortexTutorialAdapter },
    line: { definition: LINE_VORTEX_TUTORIAL, createAdapter: createLineVortexTutorialAdapter },
    knight: { definition: KNIGHT_VORTEX_TUTORIAL, createAdapter: createKnightVortexTutorialAdapter },
    branches: { definition: BRANCHES_VORTEX_TUTORIAL, createAdapter: createBranchesVortexTutorialAdapter },
    walls: { definition: WALLS_VORTEX_TUTORIAL, createAdapter: createWallsVortexTutorialAdapter },
    ignition: { definition: IGNITION_VORTEX_TUTORIAL, createAdapter: createIgnitionVortexTutorialAdapter },
    crossing: { definition: CROSSING_VORTEX_TUTORIAL, createAdapter: createCrossingVortexTutorialAdapter },
    flood: { definition: FLOOD_VORTEX_TUTORIAL, createAdapter: createFloodVortexTutorialAdapter },
    orbits: { definition: ORBITS_VORTEX_TUTORIAL, createAdapter: createOrbitsVortexTutorialAdapter },
    splitting: { definition: SPLITTING_VORTEX_TUTORIAL, createAdapter: createSplittingVortexTutorialAdapter },
    deluge: { definition: DELUGE_VORTEX_TUTORIAL, createAdapter: createDelugeVortexTutorialAdapter },
    antennas: { definition: ANTENNAS_VORTEX_TUTORIAL, createAdapter: createAntennasVortexTutorialAdapter },
    turns: { definition: TURNS_VORTEX_TUTORIAL, createAdapter: createTurnsVortexTutorialAdapter },
    jumps: { definition: JUMPS_VORTEX_TUTORIAL, createAdapter: createJumpsVortexTutorialAdapter },
};

export function hasTutorial(gameType: GameType): boolean {
    return gameType in TUTORIAL_REGISTRY;
}

export function getTutorial(gameType: GameType): TutorialEntry | undefined {
    return TUTORIAL_REGISTRY[gameType];
}

export function hasVortexTutorial(gameType: GameType): boolean {
    return gameType in VORTEX_TUTORIAL_REGISTRY;
}

export function getVortexTutorial(gameType: GameType): TutorialEntry | undefined {
    return VORTEX_TUTORIAL_REGISTRY[gameType];
}

// ── Meta-tutorials (not tied to a specific game) ──────────────────

const META_TUTORIAL_REGISTRY: Record<MetaTutorialId, TutorialEntry> = {
    basics: { definition: BASICS_TUTORIAL, createAdapter: createPlainGridTutorialAdapter },
    'vortex-hub': { definition: VORTEX_HUB_TUTORIAL, createAdapter: createVortexHubTutorialAdapter },
};

// Hub-listed meta-tutorials. 'basics' is intentionally omitted: it is redundant
// with the Welcome overview, but its tutorial stays registered for previews.
export const META_TUTORIAL_IDS: MetaTutorialId[] = ['vortex-hub'];

export function hasMetaTutorial(id: MetaTutorialId): boolean {
    return id in META_TUTORIAL_REGISTRY;
}

export function getMetaTutorial(id: MetaTutorialId): TutorialEntry | undefined {
    return META_TUTORIAL_REGISTRY[id];
}
